import { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Center,
  Container,
  Group,
  Radio,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router';
import {
  formSemaSchema,
  publicDegerlendirmeOptionsSchema,
  publicDegerlendirmeSonucSchema,
} from '@isg/validation';
import type { z } from 'zod';
import { ANLAM } from '../theme';
import { tarihGoster } from '../lib/format';
import { DemoSeridi } from '../shell/DemoSeridi';

/**
 * KAPI 2'NİN ÜÇÜNCÜ UYGULAMASI — EĞİTİM DEĞERLENDİRME PUBLIC YÜZEYİ
 * (F3B·S7 — Kapsam §16.10/9).
 *
 * OTURUM KATMANININ TAMAMEN DIŞINDA (`/bildirim` deseni): bu dosya auth/token
 * modüllerinden HİÇBİR ŞEY import etmez — QR'ı okutan katılımcıya kimlik
 * sorulmaz, kimlik çerezi/oturum anahtarı bu rotaya sızmaz (e2e kilitler).
 *
 * QR EĞİTİMİ TANIR, KATILIMCIYI DEĞİL: salondaki herkes aynı kareyi okutur;
 * yanıt sayılır, kime ait olduğu bilinmez. Aynı kişinin iki kez doldurması
 * engellenmez — kabul edilmiş bedel (engellemek kimlik taşımayı gerektirirdi).
 */
type Options = z.output<typeof publicDegerlendirmeOptionsSchema>;
type FormSema = z.output<typeof formSemaSchema>;

class PublicApiHata extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

async function publicFetch<T>(
  path: string,
  schema: { parse(value: unknown): T },
  init?: RequestInit,
): Promise<T> {
  const response = await fetch(path, init);
  if (!response.ok) {
    const govde = (await response.json().catch(() => null)) as { message?: string } | null;
    throw new PublicApiHata(
      response.status,
      govde?.message ?? `İstek başarısız (${response.status})`,
    );
  }
  return schema.parse(await response.json());
}

export function PublicDegerlendirmeScreen(): React.JSX.Element {
  const [params] = useSearchParams();
  const token = params.get('t') ?? '';

  const options = useQuery<Options, PublicApiHata>({
    queryKey: ['public-degerlendirme', token],
    queryFn: () =>
      publicFetch(
        `/api/public/degerlendirme/options?t=${encodeURIComponent(token)}`,
        publicDegerlendirmeOptionsSchema,
      ),
    retry: false,
  });

  return (
    <>
      {/* Demo şeridi oturumsuz yüzeyin KÖKÜNDE (25.09.2026 — shell/DemoSeridi.tsx);
          demo kipi kapalıyken null. Kimlik/oturum kodu bu dala yine girmez. */}
      <DemoSeridi />
      <Container size="xs" py="xl">
        <Stack data-testid="public-degerlendirme-sayfasi">
          <Title order={2}>Eğitim değerlendirmesi</Title>
          <Text size="sm" c="dimmed">
            Bu form <strong>anonimdir</strong> — kimlik bilgisi istenmez ve kaydedilmez.
            Değerlendirmenin değeri dürüstlüğündedir.
          </Text>
          {options.isPending && (
            <Center>
              <Text size="sm">Yükleniyor…</Text>
            </Center>
          )}
          {options.isError && (
            <Alert
              color={options.error.status === 404 ? ANLAM.dikkat : ANLAM.engel}
              role="alert"
              data-testid="public-degerlendirme-hata"
            >
              {options.error.status === 404
                ? 'Eğitim değerlendirme kanalı şu anda kapalı.'
                : options.error.status === 401
                  ? 'Değerlendirme bağlantısı geçersiz — lütfen QR kodunu yeniden okutun.'
                  : options.error.message}
            </Alert>
          )}
          {options.isSuccess && <DegerlendirmeFormu token={token} options={options.data} />}
        </Stack>
      </Container>
    </>
  );
}

function DegerlendirmeFormu({
  token,
  options,
}: {
  token: string;
  options: Options;
}): React.JSX.Element {
  const sema = formSemaSchema.parse(options.sema) as FormSema;
  const [cevaplar, setCevaplar] = useState<Record<string, unknown>>({});
  const [bitti, setBitti] = useState(false);
  const [hata, setHata] = useState<string | null>(null);
  const [gonderiliyor, setGonderiliyor] = useState(false);

  if (bitti) {
    return (
      <Alert color={ANLAM.olumlu} data-testid="public-degerlendirme-tesekkur">
        Değerlendirmeniz alındı — teşekkürler. Yanıtınız anonimdir.
      </Alert>
    );
  }

  const alanlar = sema.alanlar.filter((alan) => !alan.gizli).sort((a, b) => a.sira - b.sira);
  const eksik = alanlar.some(
    (alan) =>
      alan.zorunlu &&
      (cevaplar[alan.kod] === undefined ||
        cevaplar[alan.kod] === '' ||
        cevaplar[alan.kod] === null),
  );

  return (
    <Card withBorder>
      <Stack gap="sm">
        <Text fw={600} data-testid="public-degerlendirme-kunye">
          {options.egitimTuruAd} · {tarihGoster(options.tarih)}
          {options.egiticiAd ? ` · Eğitici: ${options.egiticiAd}` : ''}
          {options.egiticiKurum ? ` (${options.egiticiKurum})` : ''}
        </Text>
        {alanlar.map((alan) => (
          <Alan
            key={alan.kod}
            alan={alan}
            deger={cevaplar[alan.kod]}
            degistir={(deger) => setCevaplar((mevcut) => ({ ...mevcut, [alan.kod]: deger }))}
          />
        ))}
        {hata && (
          <Text
            size="sm"
            c={ANLAM.engel}
            role="alert"
            data-testid="public-degerlendirme-gonder-hata"
          >
            {hata}
          </Text>
        )}
        <Group>
          <Button
            data-testid="public-degerlendirme-gonder"
            disabled={eksik}
            loading={gonderiliyor}
            onClick={() => {
              setGonderiliyor(true);
              setHata(null);
              publicFetch('/api/public/degerlendirme', publicDegerlendirmeSonucSchema, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token, cevaplar }),
              })
                .then(() => setBitti(true))
                .catch((err: unknown) =>
                  setHata(err instanceof Error ? err.message : 'Gönderim başarısız'),
                )
                .finally(() => setGonderiliyor(false));
            }}
          >
            Gönder
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}

/** Şema alanının ekran karşılığı — motorun v1 tiplerinden değerlendirme
 *  formunda kullanılanlar (tekli seçim · evet/hayır · metin · uzun metin). */
function Alan({
  alan,
  deger,
  degistir,
}: {
  alan: FormSema['alanlar'][number];
  deger: unknown;
  degistir: (deger: unknown) => void;
}): React.JSX.Element {
  if (alan.tip === 'tekli_secim' || alan.tip === 'evet_hayir') {
    const secenekler =
      alan.tip === 'evet_hayir'
        ? [
            { kod: 'evet', etiket: 'Evet' },
            { kod: 'hayir', etiket: 'Hayır' },
          ]
        : (alan.secenekler ?? []);
    return (
      <Radio.Group
        label={alan.etiket}
        required={alan.zorunlu}
        data-testid={`public-alan-${alan.kod}`}
        value={
          typeof deger === 'string'
            ? deger
            : deger === true
              ? 'evet'
              : deger === false
                ? 'hayir'
                : null
        }
        onChange={(value) => degistir(alan.tip === 'evet_hayir' ? value === 'evet' : value)}
      >
        <Stack gap={4} mt={4}>
          {secenekler.map((secenek) => (
            <Radio key={secenek.kod} value={secenek.kod} label={secenek.etiket} />
          ))}
        </Stack>
      </Radio.Group>
    );
  }
  if (alan.tip === 'uzun_metin') {
    return (
      <Textarea
        label={alan.etiket}
        required={alan.zorunlu}
        minRows={3}
        data-testid={`public-alan-${alan.kod}`}
        value={typeof deger === 'string' ? deger : ''}
        onChange={(event) => degistir(event.currentTarget.value)}
      />
    );
  }
  return (
    <TextInput
      label={alan.etiket}
      required={alan.zorunlu}
      data-testid={`public-alan-${alan.kod}`}
      value={typeof deger === 'string' ? deger : ''}
      onChange={(event) => degistir(event.currentTarget.value)}
    />
  );
}
